/**
 * Fonte única dos campos obrigatórios de cadastro.
 *
 * A lista de aprovação espelha as colunas que gestorCadastroDocApproval grava em
 * doc_cadastro_wts, exceto as preenchidas pelo próprio backend (created_by, bucket,
 * pasta, status) e as que aceitam ausência: cnpj é nullable, companyName só existe
 * para pessoa jurídica e tipo_titularidade_pix tem default no backend.
 *
 * O objetivo é informativo: o operador precisa saber o que buscar manualmente antes
 * de aprovar. Nada aqui bloqueia a aprovação.
 */
export interface RequiredFieldSpec {
  /** Nomes aceitos para o campo. O payload mistura camelCase (vindo do S3) com
   *  snake_case (vindo do banco), então o mesmo dado chega com nomes diferentes. */
  keys: string[];
  label: string;
}

export const APPROVAL_REQUIRED_FIELDS: RequiredFieldSpec[] = [
  { keys: ["fullName", "nome_completo"], label: "Nome Completo" },
  { keys: ["birthday", "data_nascimento"], label: "Data de Nascimento" },
  { keys: ["cpf"], label: "CPF" },
  { keys: ["personType"], label: "Tipo de Pessoa" },
  { keys: ["email"], label: "Email" },
  { keys: ["phone", "telefone"], label: "Telefone" },
  { keys: ["ops"], label: "Operação" },
  { keys: ["pixKey"], label: "Chave PIX" },
  { keys: ["pixKeyType"], label: "Tipo de Chave PIX" },
  { keys: ["cep"], label: "CEP" },
  { keys: ["address"], label: "Endereço" },
  { keys: ["addressNumber"], label: "Número" },
  { keys: ["neighborhood"], label: "Bairro" },
  { keys: ["city"], label: "Cidade" },
  { keys: ["uf"], label: "Estado" },
  { keys: ["lgpdAgreeId"], label: "ID Acordo LGPD" },
  { keys: ["lgpdAgreeBucket"], label: "Bucket LGPD" },
  { keys: ["lgpdAgreeKeyname"], label: "Arquivo LGPD" },
];

/** Campos obrigatórios editáveis pelo formulário de atualização de cadastro. */
export const EDIT_REQUIRED_FIELDS: RequiredFieldSpec[] = [
  { keys: ["nome_completo"], label: "Nome Completo" },
  { keys: ["data_nascimento"], label: "Data de Nascimento" },
];

const isBlank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

/**
 * Retorna os rótulos dos campos obrigatórios ausentes, na ordem em que foram
 * declarados. Um campo só conta como ausente se nenhum dos seus nomes tiver valor.
 */
export const getMissingRequiredFields = (
  data: object | null | undefined,
  fields: RequiredFieldSpec[],
): string[] => {
  if (!data) return [];

  const record = data as Record<string, unknown>;

  return fields
    .filter((field) => field.keys.every((key) => isBlank(record[key])))
    .map((field) => field.label);
};
